import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";
import { Button, Card, LoadingSpinner, ErrorMessage } from "@/components/ui";
import {
  categoryService,
  CreateCategoryData,
} from "@/services/category.service";
import { useToastStore } from "@/store/toastStore";

// Schéma de validation Zod
const categorySchema = z.object({
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .min(2, "Le nom doit contenir au moins 2 caractères"),
  description: z.string().optional(),
  displayOrder: z
    .number()
    .int()
    .min(0, "L&apos;ordre d&apos;affichage doit être supérieur ou égal à 0"),
  isActive: z.boolean().default(true),
});

type CategoryFormData = z.infer<typeof categorySchema>;

/**
 * Page de formulaire pour créer ou éditer une catégorie
 */
export function CategoryFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Récupérer la catégorie si édition
  const {
    data: category,
    isLoading: categoryLoading,
    error: categoryError,
  } = useQuery({
    queryKey: ["categories", id],
    queryFn: () => categoryService.getById(id!),
    enabled: isEditing,
  });

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      description: "",
      displayOrder: 0,
      isActive: true,
    },
  });

  // Remplir le formulaire avec les données de la catégorie si édition
  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        description: category.description || "",
        displayOrder: category.displayOrder,
        isActive: category.isActive,
      });
    }
  }, [category, reset]);

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryData) => categoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      success("Catégorie créée avec succès");
      navigate("/categories");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la création de la catégorie",
      );
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateCategoryData>;
    }) => categoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["categories", id] });
      success("Catégorie modifiée avec succès");
      navigate("/categories");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la modification de la catégorie",
      );
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    const categoryData: CreateCategoryData = {
      name: data.name,
      description: data.description || undefined,
      displayOrder: data.displayOrder,
      isActive: data.isActive,
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: categoryData });
    } else {
      createMutation.mutate(categoryData);
    }
  };

  const isLoading = isEditing && categoryLoading;
  const mutationError = createMutation.error || updateMutation.error;

  if (isLoading) {
    return (
      <LoadingSpinner
        message={isEditing ? "Chargement de la catégorie..." : "Chargement..."}
      />
    );
  }

  if (isEditing && categoryError) {
    return (
      <ErrorMessage message="Erreur lors du chargement de la catégorie." />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Titre */}
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {isEditing ? "Modifier la catégorie" : "Nouvelle catégorie"}
      </Typography>

      {/* Formulaire */}
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {mutationError && (
            <ErrorMessage
              message={
                (mutationError as any)?.response?.data?.message ||
                "Une erreur est survenue lors de la sauvegarde."
              }
            />
          )}

          <Grid container spacing={3}>
            {/* Nom */}
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom de la catégorie"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                )}
              />
            </Grid>

            {/* Ordre d&apos;affichage */}
            <Grid item xs={12} md={6}>
              <Controller
                name="displayOrder"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ordre d'affichage"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    error={!!errors.displayOrder}
                    helperText={
                      errors.displayOrder?.message ||
                      "Détermine l&apos;ordre d&apos;affichage dans les listes"
                    }
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value, 10) || 0)
                    }
                    required
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Description de la catégorie..."
                  />
                )}
              />
            </Grid>

            {/* Statut actif */}
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Catégorie active"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              variant="text"
              onClick={() => navigate("/categories")}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEditing
                ? "Enregistrer les modifications"
                : "Créer la catégorie"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
